// Ubicación en el repo: src/lib/prescriptionReport.jsx
// "🖨 Imprimir receta" — mismo contenido y disposición que
// printPrescription() del prototipo (línea ~2516): formato de recetario
// compacto (135mm de ancho), membrete en grid de 3 columnas (logo | título
// | tagline — el logo se resuelve como signed URL antes de llamar a esta
// función, ver getPatientBranchLogoUrl en branchLogos.js y DECISIONS.md
// #28/#39), datos del paciente, tablas Lejos/AVsc-AVcc, ADD/ADD
// intermedia/DP, tabla Cerca, Uso/Diagnóstico/Reevaluar, observaciones y
// firma.
import { esc, printHTML } from './print';
import { calculateAge, formatDate, formatPatientName, formatProfessionalName } from './format';
import { DIAGNOSTICO_OPTS, USO_OPTS } from './prescriptions';

const cell = (v) => (v !== null && v !== undefined && v !== '' ? esc(String(v)) : '—');

function lensTableHTML(block) {
  const od = block?.od || {};
  const oi = block?.oi || {};
  return `
    <table style="width:100%; border-collapse:collapse; table-layout:fixed; font-size:13px;">
      <colgroup><col style="width:16%"><col style="width:28%"><col style="width:28%"><col style="width:28%"></colgroup>
      <thead><tr><th style="border:1px solid #999; padding:4px;"></th><th style="border:1px solid #999; padding:4px;">Esfera</th><th style="border:1px solid #999; padding:4px;">Cilindro</th><th style="border:1px solid #999; padding:4px;">Eje</th></tr></thead>
      <tbody>
        <tr><td style="border:1px solid #999; padding:4px; font-weight:700; color:#C4453C; text-align:center;">OD</td><td style="border:1px solid #999; padding:4px; text-align:center;">${cell(od.esfera)}</td><td style="border:1px solid #999; padding:4px; text-align:center;">${cell(od.cilindro)}</td><td style="border:1px solid #999; padding:4px; text-align:center;">${cell(od.eje)}</td></tr>
        <tr><td style="border:1px solid #999; padding:4px; font-weight:700; color:#2563A6; text-align:center;">OI</td><td style="border:1px solid #999; padding:4px; text-align:center;">${cell(oi.esfera)}</td><td style="border:1px solid #999; padding:4px; text-align:center;">${cell(oi.cilindro)}</td><td style="border:1px solid #999; padding:4px; text-align:center;">${cell(oi.eje)}</td></tr>
      </tbody>
    </table>`;
}

function avTableHTML(av) {
  const od = av?.od || {};
  const oi = av?.oi || {};
  return `
    <table style="width:100%; border-collapse:collapse; table-layout:fixed; font-size:13px;">
      <colgroup><col style="width:16%"><col style="width:42%"><col style="width:42%"></colgroup>
      <thead><tr><th style="border:1px solid #999; padding:4px;"></th><th style="border:1px solid #999; padding:4px;">AVsc</th><th style="border:1px solid #999; padding:4px;">AVcc</th></tr></thead>
      <tbody>
        <tr><td style="border:1px solid #999; padding:4px; font-weight:700; color:#C4453C; text-align:center;">OD</td><td style="border:1px solid #999; padding:4px; text-align:center;">${cell(od.sc)}</td><td style="border:1px solid #999; padding:4px; text-align:center;">${cell(od.cc)}</td></tr>
        <tr><td style="border:1px solid #999; padding:4px; font-weight:700; color:#2563A6; text-align:center;">OI</td><td style="border:1px solid #999; padding:4px; text-align:center;">${cell(oi.sc)}</td><td style="border:1px solid #999; padding:4px; text-align:center;">${cell(oi.cc)}</td></tr>
      </tbody>
    </table>`;
}

export function printPrescriptionReport(rx, logoUrl, signatureUrl) {
  const p = rx.patients || {};
  const prof = rx.professionals;

  // Firma: uno u otro, nunca los dos juntos — ver misma nota en examReport.jsx.
  const signatureBlockHTML = signatureUrl
    ? `<div style="margin-top:12px; display:flex; justify-content:center;">
         <img src="${esc(signatureUrl)}" style="max-height:80px; max-width:220px; object-fit:contain; display:block;" />
       </div>`
    : `<div style="margin-top:44px; display:flex; justify-content:center;">
         <div style="text-align:center; width:240px;">
           <div style="border-top:1px solid #333; padding-top:6px; font-size:13.5px;">
             ${prof ? cell(formatProfessionalName(prof, { withRole: true })) : '&nbsp;'}
           </div>
           ${prof?.rut ? `<div style="font-size:11px; color:#444; margin-top:1px;">RUT ${esc(prof.rut)}</div>` : ''}
           <div style="font-size:10.5px; color:#666; margin-top:2px; font-weight:700; letter-spacing:.03em;">FIRMA Y TIMBRE</div>
         </div>
       </div>`;

  const html = `
    <div style="font-family:'Inter',Arial,sans-serif; padding:12px 8px; width:100%; max-width:135mm; font-size:14px; color:#1a1a1a; box-sizing:border-box;">
      <div style="display:grid; grid-template-columns:1fr auto 1fr; align-items:center; border-bottom:2px solid #333; padding-bottom:9px; margin-bottom:14px;">
        <div>${logoUrl ? `<img src="${esc(logoUrl)}" style="max-height:38px; max-width:120px; object-fit:contain;" />` : ''}</div>
        <h2 style="font-family:'Space Grotesk',Arial,sans-serif; margin:0; font-size:18px; letter-spacing:.03em; text-align:center; white-space:nowrap;">RECETA DE LENTES</h2>
        <div style="color:#666; font-size:11.5px; text-align:right;">by GlobalVision</div>
      </div>

      <table style="width:100%; font-size:13.5px; margin-bottom:12px; border-collapse:collapse;">
        <tr>
          <td style="padding:4px 0; width:60%;"><strong>NOMBRE:</strong> ${cell(formatPatientName(p))}</td>
          <td style="padding:4px 0;"><strong>RUT:</strong> ${cell(p.rut)}</td>
        </tr>
        <tr>
          <td style="padding:4px 0;"><strong>EDAD:</strong> ${p.birthdate ? `${calculateAge(p.birthdate)} años` : '—'}</td>
          <td style="padding:4px 0;"><strong>FECHA:</strong> ${esc(formatDate(rx.date))}</td>
        </tr>
      </table>

      <div style="display:flex; gap:14px;">
        <div style="flex:1;"><div style="font-size:14px; font-weight:700; margin-bottom:5px;">Lejos</div>${lensTableHTML(rx.lejos)}</div>
        <div style="flex:1;"><div style="font-size:14px; font-weight:700; margin-bottom:5px;">&nbsp;</div>${avTableHTML(rx.av)}</div>
      </div>

      <div style="font-size:13.5px; margin:12px 0;">
        <strong>ADD:</strong> ${rx.add_esf != null ? rx.add_esf : '____'} ESF. &nbsp;&nbsp;&nbsp; <strong>ADD INTERMEDIA:</strong> ${rx.add_intermedia != null ? rx.add_intermedia : '____'} ESF.
      </div>
      <div style="font-size:13.5px; margin-bottom:12px;">
        <strong>DP:</strong> ${rx.dp != null ? rx.dp : '____'} mm.
      </div>

      <div style="font-size:14px; font-weight:700; margin-bottom:5px;">Cerca</div>
      ${lensTableHTML(rx.cerca)}

      <div style="display:flex; gap:26px; margin-top:14px; font-size:13.5px;">
        <div>
          <strong>USO</strong><br>
          ${USO_OPTS.map((u) => `${rx.uso === u ? '●' : '○'} ${esc(u)}<br>`).join('')}
        </div>
        <div>
          <strong>DIAGNÓSTICO</strong><br>
          ${DIAGNOSTICO_OPTS.map((d) => `${(rx.diagnostico || []).includes(d) ? '●' : '○'} ${esc(d)}<br>`).join('')}
        </div>
        <div>
          <strong>REEVALUAR</strong><br>
          ${rx.reevaluar ? '●' : '○'} Receta / corrección
        </div>
      </div>

      <div style="font-size:14px; font-weight:700; text-decoration:underline; margin:16px 0 5px;">Observaciones:</div>
      <div style="font-size:13.5px; min-height:20px;">${rx.notes ? esc(rx.notes) : '&nbsp;'}</div>

      ${signatureBlockHTML}
    </div>
  `;

  printHTML(`Receta de Lentes - ${formatPatientName(p) || 'Paciente'} - ${rx.date}`, html, { lensA5: true });
}
